import {FieldError} from '@constellation/core';
import {isEmpty} from 'lodash-es';
import {type FC, useCallback} from 'react';
import {NumericFormat} from 'react-number-format';
import {styled} from 'styled-components';

const Div = styled.div`
  display: block;
`;

const CslFieldWrapper = styled.div`
  border: none;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  margin: 0rem 0rem 1.25rem 0rem;
  padding: 0;
`;

const LabelWrapper = styled.label`
  margin-bottom: 0.75rem;
  padding: 0;
  cursor: pointer;
`;

const LabelText = styled.span`
  color: #505050;
  font-style: normal;
  margin: 0rem 0rem 0rem 0rem;
  padding: 0;
  font-family: 'Lloyds Bank Jack', system-ui, sans-serif;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.3333333333333333;
  -webkit-font-smoothing: antialiased;
`;

const InputWrapper = styled.span`
  display: block;
  position: relative;
  input {
    padding-left: 1.875rem;
  }
`;

type StyledProps = {
  borderColor: string;
};

const Input = styled(NumericFormat)<StyledProps>`
  background-color: #ffffff;
  border-radius: 0rem;
  border: 0.0625rem solid #505050;
  box-sizing: border-box;
  color: #505050;
  font-family: 'Lloyds Bank Jack', system-ui, sans-serif;
  font-size: 1.125rem;
  line-height: 1.3333333333333333;
  outline: 0.125rem solid transparent;
  outline-offset: -0.125rem;
  padding: calc(0.75rem - 0.0625rem) calc(1rem - 0.0625rem);
  -webkit-transition-duration: 0.1s;
  transition-duration: 0.1s;
  -webkit-transition-property: background-color, border-color, box-shadow;
  transition-property: background-color, border-color, box-shadow;
  -webkit-transition-timing-function: cubic-bezier(0.4, 0, 0.4, 1);
  transition-timing-function: cubic-bezier(0.4, 0, 0.4, 1);
  max-width: 21rem;
  width: 100%;
  border-color: ${({borderColor}) => borderColor};
  @media (max-width: 991px) {
    max-width: 100%;
    width: 100%;
  }
`;

const CurrencyWrapper = styled.span`
  -webkit-align-items: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  bottom: 0;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  transform: translate(1rem, -36px);
  width: 10px;
`;

const Currency = styled.span`
  color: #505050;
  font-style: normal;
  margin: 0rem 0rem 0rem 0rem;
  padding: 0;
  font-family: 'Lloyds Bank Jack', system-ui, sans-serif;
  font-weight: 700;
  font-size: 1.125rem;
  line-height: 1.3333333333333333;
`;

const SupportiveWrapper = styled.span`
  display: block;
  margin-bottom: 0.75rem;
`;

const SupportiveText = styled.span`
  color: #505050;
  font-style: normal;
  margin: 0rem 0rem 0rem 0rem;
  padding: 0;
  font-family: 'Lloyds Bank Jack', system-ui, sans-serif;
  font-weight: 400;
  font-size: 1.125rem;
  line-height: 1.3333333333333333;
`;

type CustomCurrencyFieldProps = {
  label: string;
  name: string;
  error: string;
  min: number;
  onChange: (value: number) => void;
  onFocus: () => void;
  onBlur: () => void;
  value: string;
  supportiveText: string;
};

const CustomCurrencyField: FC<CustomCurrencyFieldProps> = ({
  label,
  name,
  error,
  min,
  onChange,
  onBlur,
  onFocus,
  value,
  supportiveText,
}) => {
  const handleBorderColor = useCallback(() => {
    if (!isEmpty(error)) {
      return '#d92049';
    }
    if (isEmpty(error) && !isEmpty(value)) {
      return '#649c00';
    }

    return '';
  }, [error, value]);

  const handleOnValueChange = useCallback(
    (numberValue) => {
      const {floatValue} = numberValue;

      onChange(floatValue);
    },
    [onChange]
  );

  return (
    <Div>
      <CslFieldWrapper>
        <LabelWrapper
          aria-describedby={`${name}-hint ${name}-field-error`}
          data-csl-selector='field-label'
          htmlFor={name}
        >
          <LabelText>{label}</LabelText>
        </LabelWrapper>
        {!isEmpty(supportiveText) && (
          <SupportiveWrapper
            data-csl-selector='field-supportive'
            id='salary-hint'
          >
            <SupportiveText>{supportiveText}</SupportiveText>
          </SupportiveWrapper>
        )}
        {!isEmpty(error) && (
          <FieldError id={`${name}-field-error`}>{error}</FieldError>
        )}
        <InputWrapper>
          <Input
            allowNegative={false}
            aria-describedby={`${name}-hint ${name}-field-error`}
            aria-invalid={!isEmpty(error)}
            aria-required={true}
            borderColor={handleBorderColor()}
            decimalScale={0}
            id={name}
            min={min}
            name={name}
            thousandSeparator=','
            type='text'
            value={value}
            onBlur={onBlur}
            onFocus={onFocus}
            onValueChange={handleOnValueChange}
          />
        </InputWrapper>
        <CurrencyWrapper>
          <Currency>£</Currency>
        </CurrencyWrapper>
      </CslFieldWrapper>
    </Div>
  );
};

export default CustomCurrencyField;
